import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';

// Single source of truth — drives both visible <FAQ> section AND
// FAQPage JSON-LD. All answers grounded in facts already on this page
// (10–45% premium reduction range, FL law requiring wind mitigation
// discounts, OIR-B1-1802 form, 5-year report validity). No fabricated
// claims about specific dollar savings or guaranteed discounts.
const FAQ_ITEMS = [
  // Foundational ("what is this and how does it work")
  {
    question: 'Can impact windows lower homeowners insurance?',
    answer:
      "Yes. In Florida, certified impact windows and doors typically qualify for a wind mitigation discount on your homeowners insurance. Most homeowners see annual premium reductions of 10–45%, with the actual savings depending on your insurer, your policy, and your home's other wind mitigation features.",
  },
  {
    question: 'What is a wind mitigation inspection?',
    answer:
      "A wind mitigation inspection is a state-defined inspection performed by a licensed inspector to document your home's wind-resistance features (impact windows, roof attachment, opening protection, etc.). The inspector completes Florida's standardized form (OIR-B1-1802), which your insurance company uses to calculate your discount.",
  },
  {
    question: 'Are insurance savings guaranteed?',
    answer:
      "No — savings vary. Florida law requires insurance companies to offer discounts for certified wind mitigation features, but the percentage depends on your insurer, your specific policy, and the rest of your home's protection. Your wind mitigation report shows what your home qualifies for; your insurance company applies the corresponding discount.",
  },
  {
    question: 'Do I need all openings protected to qualify?',
    answer:
      'For the largest discount, all openings (windows, doors, garage doors, and skylights) generally need to be impact-rated or covered by approved hurricane protection. Partial protection may still qualify for a smaller discount, but full coverage typically yields the highest savings. Your wind mitigation inspector documents what is and isn\u2019t protected.',
  },
  {
    question: 'Who confirms the actual insurance discount?',
    answer:
      'Your insurance company \u2014 not your contractor or inspector \u2014 determines the actual discount applied to your policy. Once the wind mitigation inspection is complete, you submit the report to your insurer; they review it and apply the qualifying discounts at your next policy renewal (or mid-term, depending on the company).',
  },
  // Operational ("after my windows are in, what do I do")
  {
    question: 'When can I get a wind mitigation inspection?',
    answer:
      'You can schedule the inspection as soon as your impact windows are installed and the final city inspection is passed. Most homeowners schedule it within 1\u20132 weeks of project completion.',
  },
  {
    question: 'How long does it take to receive the discount?',
    answer:
      'Most insurance companies apply the discount at your next policy renewal. Some may apply it mid-term if you submit documentation before renewal. Contact your insurer to confirm their process.',
  },
  {
    question: 'Do all insurance companies offer discounts?',
    answer:
      'Yes. Florida law requires insurance companies to offer discounts for certified wind mitigation features. However, discount percentages vary by company \u2014 some offer higher discounts than others.',
  },
  {
    question: 'Can I shop for better rates with my wind mitigation report?',
    answer:
      'Absolutely. Once you have your wind mitigation report, you can shop for insurance quotes from multiple companies. Some insurers offer significantly better discounts for impact windows than others.',
  },
  {
    question: 'How long is a wind mitigation report valid?',
    answer:
      'Wind mitigation reports are typically valid for 5 years. You can continue using the same report when shopping for insurance or renewing your policy during that time.',
  },
];

export default function InsuranceSavings() {
  const pageData = {
    title: "Impact Windows & Insurance Savings: Wind Mitigation Explained",
    
    directAnswer: (
      <>
        <p>
          Installing impact windows and doors in Florida can reduce your homeowners insurance premium by <strong>10% to 45%</strong>, with most homeowners saving <strong>20–30% annually</strong>. These discounts are unlocked through a <strong>wind mitigation inspection</strong>, which documents your home's hurricane protection features and qualifies you for premium credits with your insurance company.
        </p>
      </>
    ),

    sections: [
      {
        title: "How Impact Windows Reduce Insurance Costs",
        testId: "how-it-works",
        content: (
          <p className="mb-4">
            Florida insurance companies are required by law to offer discounts for homes with certified hurricane protection features. Impact windows and doors qualify for these discounts because they:
          </p>
        ),
        bullets: [
          <><strong>Prevent Wind and Debris Damage:</strong> Impact-rated glass prevents shattering during storms, keeping wind and rain out of your home</>,
          <><strong>Protect Your Roof:</strong> By preventing interior pressure buildup, impact windows help keep your roof intact during hurricanes</>,
          <><strong>Reduce Claim Likelihood:</strong> Homes with impact windows file fewer storm-related claims, which insurers reward with lower premiums</>,
          <><strong>Meet Florida Building Code:</strong> All impact products must be Miami-Dade NOA approved and HVHZ-certified, ensuring compliance with the strictest wind codes in the U.S.</>
        ]
      },

      {
        title: "Typical Insurance Savings by Home Value",
        testId: "typical-savings",
        table: {
          headers: ["Home Value", "Annual Premium (Before)", "Discount %", "Annual Premium (After)", "Annual Savings"],
          rows: [
            ["$300,000", "$3,500", "25%", "$2,625", "$875"],
            ["$400,000", "$4,800", "25%", "$3,600", "$1,200"],
            ["$500,000", "$6,200", "28%", "$4,464", "$1,736"],
            ["$600,000", "$7,500", "30%", "$5,250", "$2,250"],
            ["$750,000", "$9,800", "32%", "$6,664", "$3,136"]
          ]
        },
        highlight: "These are estimated savings. Actual discounts vary by insurance company, location, and additional mitigation features. Discounts range from 10% to 45%."
      },

      {
        title: "What is a Wind Mitigation Inspection?",
        testId: "wind-mitigation",
        content: (
          <div className="space-y-4">
            <p>
              A wind mitigation inspection is a report performed by a licensed inspector that documents your home's hurricane protection features. The inspection verifies:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[var(--brand-slate-600)]">
              <li><strong>Impact Windows & Doors:</strong> Confirms products are certified and properly installed</li>
              <li><strong>Roof Type:</strong> Documents roof shape, covering, and attachment method</li>
              <li><strong>Roof-to-Wall Connections:</strong> Verifies how the roof is attached to the structure</li>
              <li><strong>Secondary Water Resistance (SWR):</strong> Checks for underlayment and sealing</li>
              <li><strong>Opening Protection:</strong> Confirms all windows, doors, and garage doors meet code</li>
            </ul>
            <p className="font-semibold text-[var(--brand-slate-900)] mt-4">Inspection Cost:</p>
            <p className="text-[var(--brand-slate-600)]">
              Wind mitigation inspections typically cost <strong>$75–$150</strong> and take about 30–60 minutes. The inspection must be performed after your impact windows are installed and the final city inspection is passed.
            </p>
          </div>
        )
      },

      {
        title: "How to Claim Your Insurance Discount",
        testId: "claim-discount",
        content: (
          <p className="mb-4">
            Follow these steps to unlock your insurance savings:
          </p>
        ),
        bullets: [
          <><strong>Step 1: Complete Installation</strong> — Impact windows and doors must be fully installed and pass final city inspection</>,
          <><strong>Step 2: Schedule Wind Mitigation Inspection</strong> — Hire a licensed inspector (we can provide referrals if needed)</>,
          <><strong>Step 3: Receive Inspection Report</strong> — Inspector provides a completed OIR-B1-1802 form documenting your home's features</>,
          <><strong>Step 4: Submit to Insurance Company</strong> — Send the wind mitigation report to your insurer along with installation certificates and permits</>,
          <><strong>Step 5: Receive Discount</strong> — Your insurer will recalculate your premium and apply the discount (typically at your next renewal)</>
        ],
        highlight: "Most insurance companies apply discounts retroactively to your installation date, so you won't lose any time between installation and inspection."
      },

      {
        title: "Documentation You'll Need",
        testId: "required-documentation",
        content: (
          <p className="mb-4">
            To claim your insurance discount, you'll need to provide:
          </p>
        ),
        bullets: [
          <><strong>Wind Mitigation Inspection Report (OIR-B1-1802):</strong> The official form completed by a licensed inspector</>,
          <><strong>Product Approvals (NOAs):</strong> Miami-Dade Notice of Acceptance for each window and door product installed</>,
          <><strong>Installation Certificates:</strong> Documentation showing products were installed by a licensed contractor</>,
          <><strong>Permit Sign-Off:</strong> Copy of the approved building permit and final inspection approval</>,
          <><strong>Contractor License:</strong> Copy of the installing contractor's license (CGC1524851)</>
        ],
        highlight: "We provide all installation-related documentation at project completion. You only need to arrange the wind mitigation inspection."
      },

      {
        title: "Payback Period Example",
        testId: "payback-example",
        content: (
          <div className="bg-[var(--brand-ocean-50)] rounded-xl p-6 border-2 border-[var(--brand-ocean-200)]">
            <p className="font-semibold text-[var(--brand-slate-900)] mb-4">Real Example: Kendall Single-Family Home</p>
            <ul className="space-y-2 text-[var(--brand-slate-700)]">
              <li><strong>Home Value:</strong> $450,000</li>
              <li><strong>Project Cost:</strong> $24,500 (ES Windows, 15 windows + 3 doors)</li>
              <li><strong>Annual Insurance Premium (Before):</strong> $5,200</li>
              <li><strong>Insurance Discount:</strong> 28% (~$1,456/year savings)</li>
              <li><strong>Energy Savings:</strong> ~$180/year (measured reduction in cooling costs)</li>
              <li><strong>Total Annual Savings:</strong> $1,636</li>
              <li><strong>Payback Period:</strong> 15 years (insurance + energy savings)</li>
            </ul>
            <p className="text-sm text-[var(--brand-slate-600)] mt-4 italic">
              This homeowner will save over $24,500 in the first 15 years, fully recovering the installation cost through insurance and energy savings alone — while enjoying hurricane protection, noise reduction, and increased property value.
            </p>
          </div>
        )
      },

      {
        title: "Frequently Asked Questions",
        testId: "faq",
        content: (
          <div className="space-y-4">
            {FAQ_ITEMS.map(({ question, answer }, i) => (
              <div key={i}>
                <p className="font-semibold text-[var(--brand-slate-900)] mb-2">{question}</p>
                <p className="text-[var(--brand-slate-600)]">{answer}</p>
              </div>
            ))}
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Get Your Quote",
        description: "Upload photos and receive pricing",
        url: "/get-quote?src=insurance_savings"
      },
      {
        title: "Impact Window Pricing",
        description: "See current pricing for your home",
        url: "/impact-window-cost-miami"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "See a Real Project",
        description: "Kendall case study with insurance savings",
        url: "/case-study-kendall-full-house"
      }
    ],

    ctaText: "Ready to Save on Insurance?",
    ctaSubtext: "Get your quote today and start unlocking insurance savings",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Insurance Savings" }
    ]
  };

  return (
    <>
      <SchemaMarkup
        schemas={buildFAQSchema(FAQ_ITEMS)}
        instanceId="insurance-savings-faq"
      />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
